import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { Form, Field } from "formik";
import styled from "styled-components";
import formatCurrency from "src/lib/format-currency";
import AddressLookup from "src/react/components/address-lookup";
import { EXPRESS_DELIVERY_COST_IN_PENCE } from "src/data/app-constants";
import {
  Input,
  Select,
  Checkbox,
  ValidationErrors,
  FormSection,
  FormSectionTitle,
} from "src/react/forms";
import { SubmitButton } from "src/react/components/button";
import { Flex, FlexSection } from "src/react/layout/flex";
import countries from "src/data/country-codes";
import { color } from "src/styles/variables";
import ExpressDeliveryInfoModal from "src/react/modals/express-delivery-info-modal";
import PaymentCard from "src/react/components/payment-card";
import PaymentErrorMessage from "src/react/components/payment-error-message";
import TermsAndConditionsSmallPrint from "src/react/components/terms-conditions-small-print";
import Review from "./review";

function PaymentAndDeliveryForm({
  setExpressDelivery,
  match,
  hasExpressDelivery,
  paymentError,
  status,
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
}) {
  const [manualEntry, setManualEntry] = useState(false);
  const [
    shouldShowDeliveryInfoModal,
    setShouldShowDeliveryInfoModal,
  ] = useState(false);

  useEffect(() => {
    if (manualEntry) {
      const firstLine = document.querySelector(
        `[data-selector="PaymentAndDeliveryForm-firstLineInput"]`
      );
      if (firstLine) firstLine.focus();
    }
  }, [manualEntry]);

  const handleCountryChange = (ev) => {
    const country = ev.target.value || "";
    setFieldValue("country", country);
    if (country.toLowerCase() !== "united kingdom") {
      setExpressDelivery(false);
    }
  };

  const setAddress = (address) => {
    setFieldValue("firstLine", address.firstLine);
    setFieldValue("secondLine", address.secondLine);
    setFieldValue("city", address.city);
    setFieldValue("postcode", address.postcode);
  };

  const startManualEntry = () => {
    setManualEntry(true);
  };

  const toggleDeliveryInfoModal = () => {
    setShouldShowDeliveryInfoModal((prevState) => !prevState);
  };

  const submissionError = status || paymentError;
  const shouldShowExpressDelivery = values.country === "United Kingdom";

  return (
    <Form>
      <Flex>
        <FlexSection data-recording-sensitive>
          <FormSection css="margin-bottom: 40px">
            <FormSectionTitle>Your Details</FormSectionTitle>
            <Field
              data-selector="PaymentAndDeliveryForm-firstNameInput"
              component={Input}
              name="firstName"
              label="First Name"
              placeholder="First Name"
            />
            <Field
              data-selector="PaymentAndDeliveryForm-surnameInput"
              component={Input}
              name="surname"
              label="Surname"
              placeholder="Surname"
            />
            <Field
              component={Input}
              data-selector="PaymentAndDeliveryForm-emailInput"
              name="email"
              label="Email"
              placeholder="Email"
            />
          </FormSection>
          <FormSection css="margin-bottom: 40px">
            <FormSectionTitle>Delivery Address</FormSectionTitle>
            {manualEntry && (
              <Fragment>
                <Field
                  data-selector="PaymentAndDeliveryForm-firstLineInput"
                  component={Input}
                  name="firstLine"
                  label="First Line"
                  placeholder="First Line"
                  autoComplete="shipping street-address"
                />
                <Field
                  data-selector="PaymentAndDeliveryForm-secondLineInput"
                  component={Input}
                  name="secondLine"
                  label="Second Line"
                  placeholder="Second Line"
                />
                <Field
                  data-selector="PaymentAndDeliveryForm-cityInput"
                  component={Input}
                  name="city"
                  label="City"
                  placeholder="City"
                  autoComplete="shipping address-level2"
                />
                <Field
                  data-selector="PaymentAndDeliveryForm-postcodeInput"
                  component={Input}
                  name="postcode"
                  label="Postcode"
                  placeholder="Postcode"
                  autoComplete="shipping postal-code"
                  css="text-transform: uppercase;"
                />
              </Fragment>
            )}

            <Field
              data-selector="PaymentAndDeliveryForm-countrySelect"
              component={Select}
              onChange={handleCountryChange}
              name="country"
              label="Country"
              placeholder="Country"
            >
              {countries.map(({ code, name }) => (
                <option key={code} value={name}>
                  {name}
                </option>
              ))}
            </Field>

            {!manualEntry && (
              <AddressLookup
                country={values.country}
                onManualEntry={startManualEntry}
                onSetAddress={setAddress}
              />
            )}
            {values.country === "United Kingdom" && (
              <Field
                data-selector="PaymentAndDeliveryForm-instructionsInput"
                component={Input}
                name="instructions"
                label="Safe Place Details"
                placeholder="Safe Place Details"
              />
            )}
            {shouldShowExpressDelivery && (
              <CheckboxWrapper>
                <Checkbox
                  id="expressDelivery"
                  data-selector="PaymentAndDeliveryForm-expressDeliveryCheckbox"
                  name="expressDelivery"
                  checked={hasExpressDelivery}
                  onChange={() => {
                    setExpressDelivery(!hasExpressDelivery);
                  }}
                >
                  {`Add UK Express Delivery (+${formatCurrency({
                    value: EXPRESS_DELIVERY_COST_IN_PENCE,
                  })})`}
                </Checkbox>{" "}
                <Info onClick={toggleDeliveryInfoModal}>i</Info>
              </CheckboxWrapper>
            )}
          </FormSection>
          <FormSection emphasis>
            <FormSectionTitle>Payment</FormSectionTitle>
            <PaymentCard setFieldValue={setFieldValue} />
          </FormSection>
        </FlexSection>
        <FlexSection>
          <Review editBasketLink={match.path.replace("/checkout", "/basket")}>
            <ValidationErrors touched={touched} errors={errors} />
            {submissionError && (
              <PaymentErrorMessage
                error={submissionError}
                defaultMessage={
                  <p>
                    Something's not quite right, please re-enter your details or
                    refresh the page and try again.
                  </p>
                }
              />
            )}
            <SubmitButton
              data-selector="PaymentAndDeliveryForm-SubmitButton"
              isSubmitting={isSubmitting}
              variant="primary"
              fullWidth
              onClick={startManualEntry}
            >
              Pay now
            </SubmitButton>
            {shouldShowDeliveryInfoModal && (
              <ExpressDeliveryInfoModal close={toggleDeliveryInfoModal} />
            )}
            <TermsAndConditionsSmallPrint buttonText="Pay now" />
          </Review>
        </FlexSection>
      </Flex>
    </Form>
  );
}
PaymentAndDeliveryForm.propTypes = {
  setExpressDelivery: PropTypes.func.isRequired,
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
  productsPayload: PropTypes.array,
  hasExpressDelivery: PropTypes.bool,
  paymentError: PropTypes.object,
  defaults: PropTypes.object,
  cardNumber: PropTypes.string,
};

export default PaymentAndDeliveryForm;

export const Info = styled.div`
  display: flex;
  cursor: pointer;
  border: 1px solid ${color.midGrey};
  color: ${color.midGrey};
  margin-left: 8px;
  border-radius: 50%;
  height: 17px;
  width: 18px;
  line-height: 1.2;
  align-items: baseline;
  justify-content: center;
  font-size: 13px;
`;

const CheckboxWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  margin-top: 20px;
`;
